/**
 * ProductDetailModal - Modal overlay pour afficher les détails d'un produit
 * Le ticket de caisse (30% droite) reste visible
 */

import { useEffect, useState } from 'react';
import { ecommerceApi, type PickupDates } from '../../../api/ecommerce.api';
import { api, type ProductImage } from '../../../services/api';
import type { Product } from '../../../types/ecommerce.types';
import { useEcommerceCart } from '../../../hooks/ecommerce/useEcommerceCart';
import WeightSelectionModal, { type QuantitySelection } from './WeightSelectionModal';
import { getShopStockState } from '../../../utils/shopStock';
import { useShopOrdering } from '../../../hooks/ecommerce/useShopOrdering';
import { isOrderableNow, formatJourReouverture, formatMomentBelge } from '../../../utils/shopOrdering';
import ImageLightbox from '../../ui/ImageLightbox';
import LeafLoader from '../LeafLoader';
import HowItWorksModal from '../HowItWorksModal';

// Jours (0 = dimanche) pour l'affichage des dates de retrait des précommandes.
const JOURS_LONGS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const JOURS_ABBR = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
const MOIS_ABBR = ['jan', 'fév', 'mars', 'avr', 'mai', 'juin', 'juil', 'août', 'sept', 'oct', 'nov', 'déc'];
// Semaine à la française (lundi d'abord) pour lister « jeudi, vendredi, samedi ».
const ordreLundi = (d: number) => (d + 6) % 7;
const formatDateRetrait = (iso: string) => {
  const d = new Date(iso + 'T12:00:00');
  return `${JOURS_ABBR[d.getDay()]} ${d.getDate()} ${MOIS_ABBR[d.getMonth()]}`;
};

import { useFavoris } from '../../../hooks/ecommerce/useFavoris';
import PastilleVegan from './PastilleVegan';
import PartagerProduit from './PartagerProduit';

interface ProductDetailModalProps {
  productId: string;
  onClose: () => void;
  /**
   * « page » (18/10/2026) : la fiche en page entière, à son adresse propre
   * `/shop/p/<nom>-<ref>` — sans voile, sans verrou de défilement, sans Échap ;
   * `onClose` y ramène à la boutique (après un ajout au panier, par exemple).
   */
  mode?: 'fenetre' | 'page';
}

export default function ProductDetailModal({ productId, onClose, mode = 'fenetre' }: ProductDetailModalProps) {
  const enPage = mode === 'page';
  // Favoris : la fiche est l'endroit ou l'on DECIDE qu'un produit compte —
  // on y lit la description, le prix au kilo, l'origine. Le coeur doit donc y
  // etre aussi, pas seulement sur la vignette.
  const { estFavori, basculer: basculerFavori, actif: favorisActifs } = useFavoris();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showWeightModal, setShowWeightModal] = useState(false);
  // Offre anti-gaspi choisie (lot remisé) — null = prix normal, le choix par
  // défaut : la date courte ne se prend pas sans l'avoir vue.
  const [offreId, setOffreId] = useState<string | null>(null);
  // Pain : coupé ou non (« Non coupé » par défaut, décision du 23/10/2026).
  const [coupe, setCoupe] = useState(false);
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [showLightbox, setShowLightbox] = useState(false);
  const { addItem } = useEcommerceCart();
  const ordering = useShopOrdering();
  const [pickup, setPickup] = useState<PickupDates | null>(null);
  const [showHow, setShowHow] = useState(false);

  useEffect(() => {
    loadProduct();
  }, [productId]);

  // Charger les images multi du produit
  useEffect(() => {
    if (productId) {
      api.getProductImages(productId)
        .then(images => setProductImages(images))
        .catch(() => {/* fallback sur image_url */});
    }
  }, [productId]);

  // Dates de retrait réelles d'une précommande (pain) : calculées par le
  // serveur (mêmes règles qu'au checkout : jours du boulanger, congés, heure
  // limite). Répond à « quand puis-je retirer ? » DÈS la fiche, plus au paiement.
  useEffect(() => {
    if (product?.preorder_enabled) {
      ecommerceApi.shop
        .getPickupDates([product.id])
        .then(setPickup)
        .catch(() => setPickup(null));
    } else {
      setPickup(null);
    }
  }, [product?.id, product?.preorder_enabled]);

  // Fermeture avec Escape (sauf si lightbox ouverte)
  useEffect(() => {
    if (enPage) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showLightbox) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, showLightbox, enPage]);

  // Verrouille le scroll de la page derrière la modale (évite le « glissement »
  // rubber-band iOS + le scroll-chaining vers le catalogue en arrière-plan)
  useEffect(() => {
    if (enPage) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [enPage]);

  /**
   * Le produit arrive souvent en moins de 300 ms : l'animation n'avait pas le
   * temps d'exister, elle passait pour un clignotement. On tient donc l'écran
   * d'attente au moins un cycle complet du lancer (1 s) — en dessous, autant ne
   * rien montrer du tout. Au-delà, on n'ajoute rien : si le réseau est lent,
   * c'est lui qui commande.
   */
  const DUREE_MIN_ANIMATION_MS = 1000;

  const loadProduct = async () => {
    const debut = Date.now();
    try {
      setLoading(true);
      const data = await ecommerceApi.products.getProductById(productId);
      setProduct(data);
    } catch (error) {
      console.error('Erreur chargement produit:', error);
    } finally {
      const reste = DUREE_MIN_ANIMATION_MS - (Date.now() - debut);
      if (reste > 0) await new Promise((r) => setTimeout(r, reste));
      setLoading(false);
    }
  };

  const peutEtreCoupe = (product?.options_preparation || []).includes('coupé');
  const prepOptions = peutEtreCoupe && coupe ? ['coupé'] : [];
  // Une autre fiche ouverte repart sur « Non coupé ».
  useEffect(() => setCoupe(false), [product?.id]);

  const offresLot = product?.lot_promos ?? [];
  const offre = offresLot.find((o) => o.lot_id === offreId) ?? null;

  const handleAddToCart = () => {
    if (!product) return;

    // Si produit au poids ou vendable à la pièce : ouvrir la modale de sélection
    if (product.is_weighted || product.sell_by_pieces) {
      setShowWeightModal(true);
      return;
    }

    // Sinon ajouter normalement et fermer la modal
    addItem(product, { quantity, addedBy: 'customer', prepOptions, ...(offre ? { lot: offre } : {}) });
    onClose();
  };

  const handleWeightConfirm = (selection: QuantitySelection) => {
    if (!product) return;
    if (selection.quantityUnit === 'pieces' && selection.pieceCount) {
      addItem(product, {
        quantity: 1,
        addedBy: 'customer',
        weightGrams: selection.weightGrams,
        quantityUnit: 'pieces',
        pieceCount: selection.pieceCount,
        prepOptions,
        ...(offre ? { lot: offre } : {}),
      });
    } else {
      addItem(product, {
        quantity: 1,
        addedBy: 'customer',
        weightGrams: selection.weightGrams,
        quantityUnit: 'grams',
        prepOptions,
        ...(offre ? { lot: offre } : {}),
      });
    }
    onClose();
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (loading && enPage) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LeafLoader size={56} />
      </div>
    );
  }

  if (loading) {
    // Voile plein écran à peine posé — 20 %, plus léger encore que les 30 % de
    // koro : la boutique reste franchement visible derrière, on ne perd pas le
    // fil de ce qu'on regardait. C'est le FLOU qui détache la feuille, pas
    // l'opacité ; le voile ne fait qu'apaiser les couleurs. La feuille est une
    // silhouette vert foncé pleine, elle se lit donc même sur une photo.
    return (
      <div className="fixed inset-0 z-[60] bg-[#faf7f0]/20 backdrop-blur-sm flex items-center justify-center">
        <LeafLoader size={56} />
      </div>
    );
  }

  if (!product) {
    return enPage ? (
      <div className="shop-sans max-w-xl mx-auto px-4 py-16 text-center">
        <p className="text-lg text-gray-700">Ce produit n'est plus proposé pour le moment.</p>
        <button onClick={onClose} className="mt-4 underline text-green-800 font-semibold">Voir la boutique</button>
      </div>
    ) : null;
  }

  // « À la pièce uniquement » : la fiche en parle comme d'un produit à la pièce,
  // même si la caisse le pèse. Même garde que WeightSelectionModal — sans poids
  // moyen par pièce, le réglage est ignoré au lieu de bloquer la commande.
  const piecesOnly = Boolean(
    product.ecom_pieces_only && product.sell_by_pieces && product.avg_weight_per_piece_grams,
  );

  const priceEur = product.prices?.find((p) => p.currency === 'EUR');
  const priceDisplay = priceEur ? (priceEur.amount_cents / 100).toFixed(2) : '-.--';
  // Le total suit l'offre choisie : c'est le prix que le panier retiendra.
  const prixCourant = offre ? offre.price_cents : priceEur?.amount_cents;
  const totalPrice = prixCourant != null ? ((prixCourant * quantity) / 100).toFixed(2) : '-.--';
  // La fenêtre de poids calcule ses prix sur la fiche : on lui passe le prix de l'offre.
  const produitPourPoids =
    offre && priceEur
      ? { ...product, prices: product.prices.map((p) => (p === priceEur ? { ...p, amount_cents: offre.price_cents } : p)) }
      : product;
  const dateCourte = (iso: string | null | undefined) =>
    iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : null;
  const stockState = getShopStockState(product);
  const commandable = isOrderableNow(product, ordering);

  // Images : utiliser les ProductImage si disponibles, sinon fallback sur image_url
  const images = productImages.length > 0
    ? productImages.map(img => img.url)
    : product.image_url ? [product.image_url] : [];

  // Images pour le lightbox (avec labels)
  const lightboxImages = productImages.length > 0
    ? productImages.map(img => ({
        url: img.url,
        alt: product.name,
        label: img.type === 'LABEL' ? 'Etiquette' : undefined,
      }))
    : product.image_url
      ? [{ url: product.image_url, alt: product.name }]
      : [];

  // Texte OCR de la première étiquette
  const labelText = productImages.find(img => img.type === 'LABEL' && img.label_text)?.label_text;

  return (
    <>
      <WeightSelectionModal
        isOpen={showWeightModal}
        onClose={() => setShowWeightModal(false)}
        product={produitPourPoids}
        onConfirm={handleWeightConfirm}
      />
      <HowItWorksModal open={showHow} onClose={() => setShowHow(false)} />
      {showLightbox && lightboxImages.length > 0 && (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={currentImageIndex}
          onClose={() => setShowLightbox(false)}
        />
      )}
      <div
        onClick={enPage ? undefined : handleOverlayClick}
        /* z-[60] : la barre de navigation du bas est en z-50. À égalité, c'est
           elle qui passait devant et masquait le bouton d'ajout, depuis que
           celui-ci a rejoint un pied de modal fixe. La modal de choix du poids
           était déjà au-dessus — d'où une modal qui s'affichait bien et l'autre
           non. */
        className={enPage ? 'px-3 py-4 sm:px-6 sm:py-8' : 'fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-6'}
      >
      {/* Modal Container - Centré, max 70% de largeur pour laisser le ticket visible */}
      {/* Colonne : un corps qui défile, un pied qui ne défile pas. Le bouton
          d'ajout doit rester sous la main quelle que soit la longueur de la
          description — c'est vrai sur téléphone, et ça le deviendra encore plus
          sur ordinateur quand les descriptions seront écrites. */}
      <div className={`shop-sans bg-[#faf7f0] rounded-2xl max-w-4xl w-full flex flex-col relative ${enPage ? 'mx-auto border border-black/[.08]' : 'shadow-2xl max-h-[90dvh]'}`}>
        {/* Bouton fermer (pas en page : on y revient par « ← Boutique ») */}
        {!enPage && <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-12 h-12 flex items-center justify-center bg-white hover:bg-gray-100 text-gray-600 rounded-full shadow-lg transition-all"
          title="Fermer (Esc)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2.5}
            stroke="currentColor"
            className="w-7 h-7"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>}

        {/* Contenu principal — seule cette zone défile */}
        <div className="p-8 overflow-y-auto overscroll-contain flex-1">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Colonne gauche - Images */}
            <div>
              {/* Image principale — clic ouvre le lightbox */}
              <div
                className="relative w-full aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl overflow-hidden mb-4 cursor-pointer group"
                onClick={() => images.length > 0 && setShowLightbox(true)}
              >
                {images.length > 0 ? (
                  <>
                    <img
                      src={images[currentImageIndex]}
                      alt={product.name}
                      className="w-full h-full object-contain bg-white"
                    />
                    {/* Icone zoom */}
                    <div className="absolute bottom-3 right-3 bg-black/40 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <div className="text-9xl mb-4">
                      {product.is_weighted ? '⚖️' : '📦'}
                    </div>
                    <div className="text-2xl font-semibold text-gray-600 px-8 text-center">
                      {product.name}
                    </div>
                  </div>
                )}
              </div>

              {/* Miniatures */}
              {images.length > 1 && (
                <div className="flex gap-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                        index === currentImageIndex
                          ? 'border-[#b45f2e] scale-105'
                          : 'border-black/[.12] hover:border-[#6b6550]'
                      }`}
                    >
                      <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-contain bg-white" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Colonne droite - Informations */}
            <div className="flex flex-col">
              {/* Notre production + Végan. Le badge de la vignette doit se
                  retrouver ici : le voir disparaître au clic donnerait
                  l'impression d'une annonce qui se dérobe. */}
              {(product.is_novelty || product.from_own_production || product.vegan) && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {/* La fiche REMPLACE l'objet de la liste côté client : ce qui
                      manque ici disparaît en ouvrant le produit. Le serveur pose
                      donc `is_novelty` aux deux endroits, dans le même commit. */}
                  {product.is_novelty && (
                    <span className="px-4 py-2 text-lg font-bold bg-[#2d6a4f] text-white rounded-full">
                      ✨ {product.novelty_word || 'Nouveau'}
                    </span>
                  )}
                  {product.from_own_production && (
                    <span className="px-4 py-2 text-lg font-bold bg-[#4a5c3a] text-white rounded-full">
                      Notre production
                    </span>
                  )}
                  {product.vegan && <PastilleVegan taille="fiche" />}
                </div>
              )}

              {/* Nom */}
              <div className="flex items-start gap-3 mb-4">
                <h2 className="shop-serif text-4xl font-bold text-[#3a3324] flex-1">{product.name}</h2>
                <PartagerProduit productId={product.id} name={product.name} />
                {favorisActifs && (
                  <button
                    type="button"
                    onClick={() => void basculerFavori(product.id)}
                    aria-label={estFavori(product.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                    aria-pressed={estFavori(product.id)}
                    className="shrink-0 w-12 h-12 rounded-full bg-white ring-1 ring-black/10 shadow-sm flex items-center justify-center active:scale-95 transition-transform"
                  >
                    <span className={`text-2xl leading-none ${estFavori(product.id) ? '' : 'opacity-35 grayscale'}`}>
                      {estFavori(product.id) ? '❤️' : '🤍'}
                    </span>
                  </button>
                )}
              </div>

              {/* Prix */}
              <div className="mb-6">
                {product.promo_percent && product.promo_original_price_cents ? (
                  <div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <span className="text-2xl text-[#6b6550] line-through">
                        {(product.promo_original_price_cents / 100).toFixed(2)} €
                      </span>
                      <span className="text-5xl font-bold text-[#c02626]">{priceDisplay} €</span>
                      <span className="text-sm font-bold text-white bg-[#c02626] rounded px-2 py-0.5">
                        −{product.promo_percent} %
                      </span>
                    </div>
                    {product.display_expiry_date && (
                      <div className="text-sm font-semibold text-[#c02626] mt-1">
                        🏷️ Anti-gaspi — à consommer avant le{' '}
                        {new Date(product.display_expiry_date).toLocaleDateString('fr-BE')}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-5xl font-bold text-[#b45f2e]">{priceDisplay} €</div>
                )}
                {product.unit && (
                  <div className="text-xl text-[#6b6550] mt-1">
                    {product.is_weighted ? `le ${product.unit}` : "l'unité"}
                  </div>
                )}
                {!!product.deposit_cents && product.deposit_cents > 0 && (
                  <div className="text-base text-[#6b6550] mt-1">
                    ♻️ + {(product.deposit_cents / 100).toFixed(2)} € de consigne, remboursée au retour du contenant vide
                  </div>
                )}
              </div>

              {/* Offre anti-gaspi d'un LOT : même produit, date plus courte, moins
                  cher. Le client choisit ; « prix normal » par défaut. Aucune
                  quantité affichée (« tant qu'il en reste ») : le dépassement se
                  dit à la commande, comme toute rupture. */}
              {offresLot.length > 0 && (
                <div className="mb-6 flex flex-col gap-2" role="radiogroup" aria-label="Choisir l'offre">
                  {offresLot.map((o) => {
                    const actif = offreId === o.lot_id;
                    return (
                      <button
                        key={o.lot_id}
                        type="button"
                        role="radio"
                        aria-checked={actif}
                        onClick={() => setOffreId(o.lot_id)}
                        className={`w-full text-left rounded-xl border-2 px-4 py-3 flex items-center justify-between gap-3 ${
                          actif ? 'border-[#c02626] bg-[#fdf1f1]' : 'border-[#c02626]/30 bg-white'
                        }`}
                      >
                        <span>
                          <span className="block font-bold text-[#c02626]">🏷️ Anti-gaspi −{o.percent} %</span>
                          <span className="block text-sm text-[#6b6550]">
                            {dateCourte(o.expiry_date) ? `Date du lot : ${dateCourte(o.expiry_date)} · ` : ''}tant qu'il en reste
                          </span>
                        </span>
                        <span className="text-xl font-bold text-[#c02626] whitespace-nowrap">
                          {(o.price_cents / 100).toFixed(2)} €
                        </span>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    role="radio"
                    aria-checked={offreId === null}
                    onClick={() => setOffreId(null)}
                    className={`w-full text-left rounded-xl border-2 px-4 py-3 flex items-center justify-between gap-3 ${
                      offreId === null ? 'border-[#4a5c3a] bg-[#eef2e8]' : 'border-black/10 bg-white'
                    }`}
                  >
                    <span>
                      <span className="block font-bold text-[#3a3324]">Prix normal</span>
                      {dateCourte(product.lot_normal_expiry_date) && (
                        <span className="block text-sm text-[#6b6550]">
                          Date du lot : {dateCourte(product.lot_normal_expiry_date)} ou plus tard
                        </span>
                      )}
                    </span>
                    <span className="text-xl font-bold text-[#3a3324] whitespace-nowrap">{priceDisplay} €</span>
                  </button>
                </div>
              )}

              {/* Stock : ni quantité ni « il en reste N » (le stock restant ne
                  regarde pas le client). Seul « épuisé » subsiste, en filet, pour
                  un article tombé à 0 pendant la consultation — on ne fait pas
                  payer un produit qui vient de partir. */}
              {stockState === 'out' && (
                <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#6b6550]/10 border border-[#6b6550]/30">
                  <span className="text-base font-bold text-[#5c5744]">
                    Épuisé pour le moment
                  </span>
                </div>
              )}

              {!commandable && (
                <div className="mb-6 px-4 py-3 rounded-xl bg-[#4a5c3a]/10 border border-[#4a5c3a]/30">
                  <div className="text-base font-bold text-[#4a5c3a]">
                    Commandable {formatJourReouverture(ordering?.next_open_at)}
                  </div>
                  <div className="text-sm text-[#4a5c3a] mt-1">
                    Ce rayon rouvre
                    {ordering?.next_open_at ? ` ${formatMomentBelge(ordering.next_open_at)}` : ' aux jours d’ouverture'}.
                    {' '}Le reste du catalogue reste commandable dès maintenant.
                  </div>
                </div>
              )}

              {stockState === 'preorder' && (() => {
                const weekdays = (pickup?.constraints?.[0]?.weekdays ?? [])
                  .slice()
                  .sort((a, b) => ordreLundi(a) - ordreLundi(b));
                const prochaines = (pickup?.dates ?? []).slice(0, 3);
                return (
                  <div className="mb-6 px-4 py-3 rounded-xl bg-[#4a5c3a]/10 border border-[#4a5c3a]/30">
                    <div className="flex items-center gap-2 text-base font-bold text-[#4a5c3a]">
                      <span>📅</span>
                      <span>Sur commande</span>
                    </div>
                    {weekdays.length > 0 ? (
                      <div className="text-sm text-[#4a5c3a] mt-1">
                        Retrait en magasin&nbsp;:{' '}
                        <span className="font-semibold">{weekdays.map((d) => JOURS_LONGS[d]).join(', ')}</span>.
                        {prochaines.length > 0 && (
                          <>
                            {' '}Prochaines dates&nbsp;:{' '}
                            <span className="font-semibold">{prochaines.map(formatDateRetrait).join(' · ')}</span>.
                          </>
                        )}
                        {/* Cadence mensuelle (seigle : 1er et 3e vendredis) : dite
                            avant le panier, puisqu'elle repousse TOUTE la commande. */}
                        {pickup?.retards?.[0]?.raison && (
                          <div className="mt-1 font-semibold">
                            ⏳ Ce produit {pickup.retards[0].raison} : dans votre panier, il fixe la date de retrait de
                            toute la commande.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-[#4a5c3a] mt-1">
                        Vous choisirez le jour de retrait à la commande.
                      </div>
                    )}
                    <button
                      onClick={() => setShowHow(true)}
                      className="text-sm underline underline-offset-2 text-[#4a5c3a] font-semibold mt-1"
                    >
                      Comment ça marche&nbsp;?
                    </button>
                  </div>
                );
              })()}

              {/* Description */}
              {product.description && (
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-[#3a3324] mb-2">Description</h3>
                  <p className="text-[#4a4530] leading-relaxed whitespace-pre-line">
                    {product.description}
                  </p>
                </div>
              )}

              {/* Texte OCR étiquette */}
              {labelText && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h3 className="text-sm font-bold text-amber-900 mb-2">Informations de l'etiquette</h3>
                  <p className="text-sm text-amber-800 leading-relaxed whitespace-pre-line">
                    {labelText}
                  </p>
                </div>
              )}

              {/* Sélecteur de quantité - uniquement pour produits non pesés et non vendables à la pièce */}
              {!product.is_weighted && !product.sell_by_pieces ? (
                <div className="mb-6">
                  <label className="block text-lg font-medium text-[#4a4530] mb-2">
                    Quantité
                  </label>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-12 h-12 flex items-center justify-center bg-[#f0e9d8] hover:bg-[#e5dcc4] rounded-lg font-bold text-2xl text-[#4a5c3a] transition-colors"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-20 h-12 text-center text-2xl font-bold border-2 border-black/[.15] bg-white rounded-lg focus:outline-none focus:border-[#b45f2e]"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-12 h-12 flex items-center justify-center bg-[#f0e9d8] hover:bg-[#e5dcc4] rounded-lg font-bold text-2xl text-[#4a5c3a] transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{product.sell_by_pieces ? '🥕' : '⚖️'}</span>
                    <span className="font-semibold text-blue-900">
                      {product.sell_by_pieces && product.is_weighted && !piecesOnly
                        ? 'Choisissez votre format'
                        : product.sell_by_pieces
                        ? 'Produit vendu à la pièce'
                        : 'Produit vendu au poids'}
                    </span>
                  </div>
                  <p className="text-sm text-blue-700">
                    {product.sell_by_pieces && product.is_weighted && !piecesOnly
                      ? 'Choisissez entre le poids souhaité ou le nombre de pièces. Le prix sera ajusté au poids réel lors de la préparation.'
                      : product.sell_by_pieces
                      ? `Choisissez le nombre de pièces (~${product.avg_weight_per_piece_grams || 200}g par pièce). Le prix sera ajusté au poids réel.`
                      : 'Choisissez le poids souhaité (100g, 250g, 500g, 1kg ou personnalisé). Le poids final sera ajusté lors de la préparation.'}
                  </p>
                </div>
              )}

            </div>
          </div>

          {/* Les suggestions par catégorie restent à écrire. Le bloc était rendu
              sans condition sur les 1 097 fiches, juste au-dessus du bouton
              d'achat : un client inconnu y lisait « Section à venir », c'est-à-dire
              une note de chantier sur une boutique ouverte. Retiré jusqu'à ce
              qu'il ait quelque chose à montrer. */}
        </div>

        {/* Pied fixe : le bouton d'ajout ne quitte jamais l'écran. Il porte le
            total, seul repère qui compte au moment de décider. */}
        <div className="shrink-0 border-t border-black/[.08] bg-[#faf7f0] rounded-b-2xl px-6 py-4 safe-area-bottom">
          {peutEtreCoupe && stockState !== 'out' && commandable && (
            // À côté du bouton, là où l'on décide : « Non coupé » par défaut.
            <div role="radiogroup" aria-label="Coupe du pain" className="grid grid-cols-2 gap-2 mb-3">
              {[
                { valeur: false, libelle: 'Non coupé' },
                { valeur: true, libelle: '🔪 Coupé' },
              ].map((o) => (
                <button
                  key={o.libelle}
                  type="button"
                  role="radio"
                  aria-checked={coupe === o.valeur}
                  onClick={() => setCoupe(o.valeur)}
                  className={`min-h-[48px] rounded-xl border-2 text-base font-semibold transition-colors ${
                    coupe === o.valeur
                      ? 'border-[#4a5c3a] bg-[#eef2e8] text-[#2f3b24]'
                      : 'border-black/10 bg-white text-[#5b5546]'
                  }`}
                >
                  {o.libelle}
                </button>
              ))}
            </div>
          )}
          <button
            onClick={handleAddToCart}
            disabled={stockState === 'out' || !commandable}
            className={`w-full py-4 rounded-xl font-bold text-xl md:text-2xl shadow-lg transition-all ${
              stockState === 'out' || !commandable
                ? 'bg-[#d8d3c4] text-[#7a7461] cursor-not-allowed'
                : 'bg-[#b45f2e] hover:bg-[#9d5227] text-white active:scale-95'
            }`}
          >
            {stockState === 'out'
              ? 'Épuisé pour le moment'
              : !commandable
              ? `Commandable ${formatJourReouverture(ordering?.next_open_at)}`
              : product.is_weighted || product.sell_by_pieces
              ? `Choisir ${product.sell_by_pieces ? 'la quantité' : 'le poids'} · ${priceDisplay} €${product.is_weighted ? '/kg' : ''}`
              : `Ajouter au panier · ${totalPrice} €`}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
